import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonService } from '../../services/common.service';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import {
  DomSanitizer,
  SafeResourceUrl,
  SafeUrl,
} from '@angular/platform-browser';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-qr-attendance-and-result-sheet',
  templateUrl: './qr-attendance-and-result-sheet.component.html',
  styleUrls: ['./qr-attendance-and-result-sheet.component.scss'],
})
export class QrAttendanceAndResultSheetComponent implements OnInit, OnDestroy {
  assessApplyId: number;
  assessorRefId: number;
  attendanceAndResultSheetInfo: any;

  photoUrls: string[] = []; // or map if you want by id/index
  safePhotoUrls: (SafeUrl | null)[] = [];
  private createdObjectUrls: string[] = [];

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private commonService: CommonService,
    private notification: NzNotificationService,
    private sanitizer: DomSanitizer,
  ) {
    this.assessApplyId = Number(
      this.activatedRoute.snapshot.queryParamMap.get('assessApplyId'),
    );
    this.assessorRefId = Number(
      this.activatedRoute.snapshot.queryParamMap.get('assessorRefId'),
    );
  }

  ngOnInit(): void {
    this.getAttendanceAndResultSheetData();
  }

  getAttendanceAndResultSheetData() {
    this.commonService
      .readAttendanceAndResultSheetData(this.assessApplyId, this.assessorRefId)
      .subscribe({
        next: (res) => {
          if (res.success) {
            this.attendanceAndResultSheetInfo = res.data;
            this.loadPhotos();
          } else {
            this.notification.error('Failed!', res.message);
            console.warn('No data found');
          }
        },
        error: (error) => {
          this.notification.error('Failed', error);
        },
      });
  }

  async loadPhotos() {
    this.safePhotoUrls = [];

    for (let i = 0; i < this.attendanceAndResultSheetInfo.length; i++) {
      const rawUrl = this.attendanceAndResultSheetInfo[i]?.photoUrl;

      if (!rawUrl) {
        this.safePhotoUrls[i] = null;
        continue;
      }

      try {
        const url = rawUrl.trim();

        // Case 1: photoUrl itself is already base64 data URL
        if (url.startsWith('data:image/')) {
          this.safePhotoUrls[i] = this.sanitizer.bypassSecurityTrustUrl(url);
          continue;
        }

        // Case 2: photoUrl is a pure base64 string without data:image prefix
        if (url.startsWith('iVBOR') || url.startsWith('/9j/')) {
          const mimeType = url.startsWith('/9j/') ? 'image/jpeg' : 'image/png';
          this.safePhotoUrls[i] = this.sanitizer.bypassSecurityTrustUrl(
            `data:${mimeType};base64,${url}`,
          );
          continue;
        }

        // Case 3: photoUrl is a file path/url
        const fullUrl =
          url.startsWith('http://') || url.startsWith('https://')
            ? url
            : environment.fileServiceApiUrl + url;

        const response = await fetch(fullUrl);

        if (!response.ok) {
          console.error('Failed to fetch image:', response.status, fullUrl);
          this.safePhotoUrls[i] = null;
          continue;
        }

        const contentType = response.headers.get('content-type') || '';

        // Case 4: backend/file server returns txt file containing base64/data URL
        if (
          contentType.includes('text') ||
          fullUrl.toLowerCase().endsWith('.txt')
        ) {
          const text = (await response.text()).trim();

          if (text.startsWith('data:image/')) {
            this.safePhotoUrls[i] = this.sanitizer.bypassSecurityTrustUrl(text);
          } else if (text.startsWith('iVBOR') || text.startsWith('/9j/')) {
            const mimeType = text.startsWith('/9j/')
              ? 'image/jpeg'
              : 'image/png';
            this.safePhotoUrls[i] = this.sanitizer.bypassSecurityTrustUrl(
              `data:${mimeType};base64,${text}`,
            );
          } else {
            console.error(
              'Text file does not contain valid image base64:',
              fullUrl,
            );
            this.safePhotoUrls[i] = null;
          }

          continue;
        }

        // Case 5: real image file response
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);

        this.createdObjectUrls.push(objectUrl);
        this.safePhotoUrls[i] =
          this.sanitizer.bypassSecurityTrustUrl(objectUrl);
      } catch (e) {
        console.error('Error loading image:', e);
        this.safePhotoUrls[i] = null;
      }
    }
  }

  ngOnDestroy(): void {
    this.createdObjectUrls.forEach((url) => URL.revokeObjectURL(url));
  }
  
}
